import type { ReactElement } from 'react';
import { EmptyState } from '@/components/kit';
import { useAuthStore } from '@/stores/useAuthStore';
import { useShellChrome } from '@/layouts/shellChrome';
import { Permission } from '@/types/user';
import type { DesignRoleId } from '@/config/designRoles';

/**
 * A ROLE'S ROUTES ARE NOT PUBLIC BECAUSE ITS RAIL DOOR IS HIDDEN.
 *
 * The rail gates each door on a permission (designRoles.ts `requires`), but a
 * hidden door is a hidden LINK, not a closed one: `/clinic-manager/settings/
 * subscription` typed into the bar still mounted the page. Most of those
 * screens then refused correctly, because the API refuses them — but a screen
 * whose data comes from an endpoint open to every member (Subscription reads
 * `/auth/me/entitlements`) rendered as if the reader were the manager, with
 * the parts it could not read shown as zeros.
 *
 * So the subtree is gated once, here, at the mount. The denial is the same
 * sentence the settings screens already use when the record is refused, for
 * the same reason: it says whose the record is, and that nothing changed.
 */
interface SurfaceGate {
  needs: Permission;
  /** What the reader was trying to open, in the screen's own words. */
  title: string;
  sub: string;
}

const SURFACE_REQUIRES: Partial<Record<DesignRoleId, SurfaceGate>> = {
  /* Owner and Clinic Manager hold hospital_settings:read; no other role does.
     The manager's own Today, its alerts, its audit log and System Settings are
     one hat, so they are gated as one. */
  'clinic-manager': {
    needs: Permission.HOSPITAL_SETTINGS_READ,
    title: 'System Settings',
    sub: 'System Settings belongs to the clinic’s owner and its manager. This account is neither, so nothing about this clinic is shown here — and nothing has been changed.',
  },
  /* The shelf is readable far more widely than it is writable, so the SUBTREE
     is gated on the read. The screens inside it gate their own verbs: Receive
     and the write-off are drawn only for `stock:write`, which is why a nurse
     opening the shelf by URL gets the shelf, not a refusal. */
  'stock-manager': {
    needs: Permission.STOCK_READ,
    title: 'Stock',
    sub: 'The clinic’s shelf is shown to the people who use or keep it. This account holds neither, so no quantity, batch or expiry is shown here — and nothing has been changed.',
  },
  /* The worklist reads orders; front desk and billing hold no order read. */
  technician: {
    needs: Permission.IMAGING_ORDER_READ,
    title: 'Worklist',
    sub: 'The investigation worklist is for the people who perform or order the tests. This account does neither, so no order or patient is shown here — and nothing has been changed.',
  },
};

/** `/clinic-manager/settings/money` → `clinic-manager`. */
function surfaceOf(path: string): DesignRoleId | undefined {
  const first = path.replace(/^\//, '').split('/')[0];
  return first ? (first as DesignRoleId) : undefined;
}

/**
 * The refused screen is still a screen: it publishes a title, so the top bar
 * says where the reader is instead of going blank, and it draws inside the
 * kit's own content column so the card looks like every other empty state.
 */
function Refused({ gate }: { gate: SurfaceGate }) {
  useShellChrome({ title: gate.title, raw: true }, ['refused', gate.title]);
  return (
    <div className="maie desk">
      <div className="dcontent scroll">
        <EmptyState
          className="mt12"
          title={`This clinic’s ${gate.title} is not yours to read`}
          sub={gate.sub}
        />
      </div>
    </div>
  );
}

export function RoleRouteGuard({ path, children }: { path: string; children: ReactElement }) {
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const surface = surfaceOf(path);
  const gate = surface ? SURFACE_REQUIRES[surface] : undefined;
  if (!gate || roleCheck.hasPermission(gate.needs)) return children;
  return <Refused gate={gate} />;
}
